import "../helpers/publicPath";
import React, { useReducer } from "react";
import { createPortal } from "react-dom";
import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import { ThemeProvider, StyledEngineProvider } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import { theme } from "../theme";
import { NavView } from "../features/nav/NavView";
import { RenderKind } from "../types/render-kind";
import { CartProvider } from "../features/cart";
import { ProductSlider } from "../modules/ProductSlider";
import { ImageGridSlider as ImageGridSliderModule } from "../modules/ImageGridSlider";
import { PressModule } from "../modules/PressModule";
import { UserTestimonialModule } from "../modules/UserTestimonialModule";
import { initSentry } from "../helpers/errors";
import { ProductDiscovery } from "../modules/ProductDiscovery";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "../helpers/fetch";
import { Customer, CustomerProvider } from "../features/CustomerContext";
import { GrowthBookProvider } from "@growthbook/growthbook-react";
import { initializeAnnouncementBar } from "../modules/announcement-bar";
import { trimPortalProps } from "../helpers/droppsApp";
import { growthBook } from "../helpers/growthbook";
import { initializeHeroSlideshow } from "../modules/heroSlideshow";
import { initNativeProductCards } from "../features/product/NativeProductCard";
export { getActiveTests } from "../helpers/growthbook";
export { setCartAndCheckout } from "../services/shopifyCart";
export { getUTMAttributes } from "../helpers/utm";
export { generateRechargeUrl } from "../features/portal/RechargeApiActions";
export { getProduct } from "../helpers/droppsApp";

initSentry();
initializeAnnouncementBar();
initializeHeroSlideshow();

// Reducer to manage the renders array
const rendersReducer = (state: any[]) => state;

const PortalComponent = (render: RenderKind) => {
  switch (render.kind) {
    case "image-grid-slider":
      return <ImageGridSliderModule {...trimPortalProps(render)} />;
    case "nav-view":
      return <NavView {...trimPortalProps(render)} />;
    case "press-testimonials":
      return <PressModule {...trimPortalProps(render)} />;
    case "product-slider":
      return (
        <ProductSlider
          showProductCardListCollection={true}
          {...trimPortalProps(render)}
        />
      );
    case "user-testimonials":
      return <UserTestimonialModule {...trimPortalProps(render)} />;
    case "product-discovery":
      return <ProductDiscovery {...trimPortalProps(render)} />;
    default:
      return <></>;
  }
};

// Drain queue from inline section scripts that ran before this deferred bundle loaded (same as product/collection/bundle)
const initialRenders = (() => {
  if (typeof window === "undefined" || !(window as any).__reactRenderQueue)
    return [];
  const queue = (window as any).__reactRenderQueue;
  (window as any).__reactRenderQueue = [];
  return queue as any[];
})();

const DroppsApp = () => {
  const [renders] = useReducer(rendersReducer, initialRenders);

  return (
    <>
      {renders.map((x: any) => {
        const el = document.getElementById(x.id);
        if (!el) {
          throw new Error(`expected id ${x.id} to exist`);
        }
        const key = `${x.id}-${x.kind}`;
        return createPortal(
          <Sentry.ErrorBoundary showDialog>
            <PortalComponent {...x} />
          </Sentry.ErrorBoundary>,
          el,
          key
        );
      })}
    </>
  );
};

export const render = ({ user }: { user: Customer | null }) => {
  if (user) {
    Sentry.setUser({
      id: user.id,
      email: user.email,
      name: `${user.firstName} ${user.lastName}`,
    });
  }

  const container = document.getElementById("dropps-app-root");
  if (container === null) {
    throw new Error("dropps-app-root not found");
  }

  const root = createRoot(container);
  root.render(
    <React.StrictMode>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <QueryClientProvider client={queryClient}>
            <CustomerProvider customer={user}>
              <GrowthBookProvider growthbook={growthBook}>
                <CartProvider>
                  <DroppsApp />
                </CartProvider>
              </GrowthBookProvider>
            </CustomerProvider>
          </QueryClientProvider>
        </ThemeProvider>
      </StyledEngineProvider>
    </React.StrictMode>
  );

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initNativeProductCards, {
      once: true,
    });
  } else {
    initNativeProductCards();
  }
};
